// - 피그마 디자인 시스템의 경우, Chip 컴포넌트만 명세되어 있지만,
//   개발팀 내부적으로는 이를 기능적인 관점에서 구분하여, 일반 Chip 컴포넌트와
//   파일 관련 기능이 필요한 FileUploadChip 컴포넌트로 구분하여 사용합니다
// - Chip 컴포넌트와 FileUploadChip 컴포넌트는 서로 같은 스타일을 공유하므로 하나의 파일 내에서 관리합니다

export type ColorType = 'primary' | 'gray';
export type VariantType = 'fill' | 'line';
export type SizeType = 'sm' | 'md' | 'lg';

/** 디자인 토큰 원본값 (className 이 아닌 실제 px 값이 필요한 곳을 위해 유지) */
export const TextWrapperSizeStyles = {
  sm: {
    height: '26px',
    padding: '0 12px',
    fontSize: '12px',
    letterSpacing: 'normal',
    gap: '4px',
  },
  md: {
    height: '45px',
    padding: '0 20px',
    fontSize: '18px',
    letterSpacing: '-0.36px',
    gap: '6px',
  },
  lg: {
    height: '56px',
    padding: '0 28px',
    fontSize: '20px',
    letterSpacing: '-0.4px',
    gap: '6px',
  },
};

export const IconWrapperSizeStyles = {
  sm: {
    marginRight: '-6px',
    paddingLeft: '12px',
  },
  md: { marginRight: '-12px', paddingLeft: '20px' },
  lg: { marginRight: '-20px', paddingLeft: '28px' },
};

/* ------------------------------------------------------------------ *
 * className 조합
 *
 * 원본 emotion 에서는 ChipContainer 안에 `p { color }`, `small { color }`,
 * `&:disabled { ... }`, 그리고 마지막에 VariantColorStyles[variant][color] 가
 * 차례로 선언되어 있었고, 뒤에 선언된 쪽이 이겼다.
 * Tailwind 유틸리티는 전부 !important 라서 같은 속성을 두 번 내보내면
 * className 순서가 아니라 스타일시트 생성 순서로 승자가 정해진다.
 * => 같은 속성은 반드시 하나만 남기고, 승자를 아래 함수에서 JS 로 계산한다.
 *
 * 자손 셀렉터(p / small / IconWrapper)는 각 요소에 직접 클래스를 붙이는 방식으로 옮겼고,
 * `&:disabled` 하위 규칙은 ChipContainer 에 붙은 `group` 을 통해 group-disabled: 로 옮겼다.
 * ------------------------------------------------------------------ */

const CONTAINER_HEIGHT_CLASS: Record<SizeType, string> = {
  sm: 'h-[26px]',
  md: 'h-[45px]',
  lg: 'h-[56px]',
};

/** variant/color 별 배경·테두리·hover. emotion 의 @media (hover:hover) 는 v4 hover: 와 동일하다 */
const VARIANT_COLOR_CLASS: Record<VariantType, Record<ColorType, string>> = {
  fill: {
    primary: 'bg-apps-corePrimary04 hover:bg-apps-corePrimary03',
    gray: 'bg-apps-coreGray01 hover:bg-apps-coreGray02',
  },
  line: {
    primary:
      'border border-apps-corePrimary01 bg-apps-coreGrayWhite hover:bg-apps-corePrimary04 disabled:border-apps-coreProductBorder',
    gray: 'border border-apps-coreProductBorder bg-apps-coreGrayWhite hover:bg-apps-coreGray07',
  },
};

/**
 * :disabled 배경.
 * 기본값은 coreProductDisableContainer 였고, fill/gray 만 뒤에서 coreGray02 로 덮어썼다.
 * 두 유틸리티를 동시에 내보내면 순서 싸움이 나므로 승자를 여기서 하나만 고른다.
 */
function disabledBackgroundClass(variant: VariantType, color: ColorType) {
  if (variant === 'fill' && color === 'gray') {
    return 'disabled:bg-apps-coreGray02';
  }
  return 'disabled:bg-apps-coreProductDisableContainer';
}

/**
 * `transition: 0.3s` 단축 속성 그대로 유지.
 * Tailwind 의 transition 유틸리티는 property/timing-function 까지 지정해 값이 달라진다.
 */
export function chipContainerClass({
  color,
  variant,
  size,
}: {
  color: ColorType;
  variant: VariantType;
  size: SizeType;
}) {
  return [
    'group flex max-w-full items-center rounded-[48px] px-0 py-[2px] [transition:0.3s]',
    CONTAINER_HEIGHT_CLASS[size],
    'disabled:pointer-events-none disabled:select-none',
    disabledBackgroundClass(variant, color),
    VARIANT_COLOR_CLASS[variant][color],
  ].join(' ');
}

const TEXT_WRAPPER_SIZE_CLASS: Record<SizeType, string> = {
  sm: 'gap-[4px] px-[12px] py-0 tracking-normal',
  md: 'gap-[6px] px-[20px] py-0 tracking-[-0.36px]',
  lg: 'gap-[6px] px-[28px] py-0 tracking-[-0.4px]',
};

export function chipTextWrapperClass(size: SizeType) {
  return `flex max-w-full items-center overflow-hidden leading-[160%] ${TEXT_WRAPPER_SIZE_CLASS[size]}`;
}

const CHIP_TEXT_SIZE_CLASS: Record<SizeType, string> = {
  sm: 'text-[12px]',
  md: 'text-[18px]',
  lg: 'text-[20px]',
};

/**
 * ChipText(p) 색.
 * 기본 `p { color: coreGrayBlack }` 뒤에 fill/gray 의 `p { color: coreGray06 }` 이 왔으므로
 * fill/gray 만 coreGray06 이 이긴다.
 * :disabled 도 기본 coreGray04 뒤에 fill/gray 의 흰색 + opacity 0.4 가 온다.
 */
function chipTextColorClass(variant: VariantType, color: ColorType) {
  if (variant === 'fill' && color === 'gray') {
    return 'text-apps-coreGray06 group-disabled:text-apps-coreGrayWhite group-disabled:opacity-40';
  }
  return 'text-apps-coreGrayBlack group-disabled:text-apps-coreGray04';
}

/**
 * `-webkit-line-clamp: 1` 은 display:-webkit-box 없이 선언되어 있어 실제로는 동작하지 않는다.
 * Tailwind 의 line-clamp-1 은 display/overflow/box-orient 까지 바꾸므로 쓰지 않고,
 * 원본과 같은 잠재 no-op 으로 남기기 위해 arbitrary property 로 옮긴다.
 */
export function chipTextClass({
  size,
  variant,
  color,
}: {
  size: SizeType;
  variant: VariantType;
  color: ColorType;
}) {
  return [
    'max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-medium [-webkit-line-clamp:1]',
    CHIP_TEXT_SIZE_CLASS[size],
    chipTextColorClass(variant, color),
  ].join(' ');
}

/**
 * ChipSubText(small) 색.
 * 기본 `small { color: coreGray03 }` 뒤에 fill/gray 의 `small { color: coreGray06 }` 이 온다.
 * :disabled 는 fill/gray 도 별도 규칙이 없어 항상 coreGray04.
 */
function chipSubTextColorClass(variant: VariantType, color: ColorType) {
  if (variant === 'fill' && color === 'gray') {
    return 'text-apps-coreGray06';
  }
  return 'text-apps-coreGray03';
}

export function chipSubTextClass({
  variant,
  color,
}: {
  variant: VariantType;
  color: ColorType;
}) {
  return [
    'shrink-0 overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-normal [-webkit-line-clamp:1]',
    chipSubTextColorClass(variant, color),
    'group-disabled:text-apps-coreGray04',
  ].join(' ');
}

const ICON_WRAPPER_LEFT_CLASS: Record<SizeType, string> = {
  sm: 'mr-[-6px] pl-[12px]',
  md: 'mr-[-12px] pl-[20px]',
  lg: 'mr-[-20px] pl-[28px]',
};

/**
 * position 이 'left' 이고 size 가 있을 때만 왼쪽 배치 값을 쓰고,
 * 그 외에는 오른쪽 배치 값을 쓰던 원본 분기를 그대로 유지한다.
 */
export function chipIconWrapperClass({
  position,
  size,
}: {
  position: 'left' | 'right';
  size?: SizeType;
}) {
  const placement =
    position === 'left' && size
      ? ICON_WRAPPER_LEFT_CLASS[size]
      : 'ml-[-6px] pr-[20px]';

  return `flex h-full w-fit shrink-0 items-center group-disabled:opacity-40 ${placement}`;
}
